import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import {
  ActionBar,
  Badge,
  Button,
  FormRow,
  Input,
  Label,
  Muted,
  Page,
  PageHeader,
  PageHeaderMain,
  Subtitle,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  Title,
  Toast,
  Toolbar,
} from '../components/ui';

interface ClaimItem {
  id: string;
  displayName: string;
  username: string;
  quantity: number;
  note: string;
  status: string;
  createdAt: string;
}

const OBS_CSS = `body {
  background-color: rgba(0, 0, 0, 0);
  margin: 0px auto;
  overflow: hidden;
}`;

const OracleMonitorPage: React.FC = () => {
  const [monitorUrl, setMonitorUrl] = useState('');
  const [pending, setPending] = useState<ClaimItem[]>([]);
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [{ data: settings }, { data: list }] = await Promise.all([
      api.get('/oracle-claims/monitor-settings'),
      api.get('/oracle-claims/pending'),
    ]);
    setMonitorUrl(settings.monitorUrl || '');
    setPending(list.pending || []);
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 4000);
    return () => window.clearInterval(id);
  }, [load]);

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setToast('Copiado al portapapeles');
  };

  const regenerate = async () => {
    if (!window.confirm('¿Regenerar URL del monitor OBS? Tendrás que actualizar la fuente en OBS.')) return;
    const { data } = await api.post('/oracle-claims/monitor-settings/regenerate');
    setMonitorUrl(data.monitorUrl || '');
    setToast('URL regenerada');
  };

  const deliver = async (id: string) => {
    setBusyId(id);
    try {
      await api.patch(`/oracle-claims/${id}/deliver`);
      setToast('Entregado — carta descontada');
      load();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Error al entregar');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id: string) => {
    setBusyId(id);
    try {
      await api.patch(`/oracle-claims/${id}/reject`);
      setToast('No entregado — sin descuento');
      load();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Page>
      <PageHeader>
        <PageHeaderMain>
          <Title>Oráculos monitor</Title>
          <Subtitle>
            Cola de pedidos por chat (!pediroraculo). Entregado descuenta carta; no entregado, no.
            Al publicar en Oráculos, usá Regalo para no descontar dos veces.
          </Subtitle>
        </PageHeaderMain>
      </PageHeader>

      {toast && <Toast onClick={() => setToast('')}>{toast}</Toast>}

      <Surface style={{ marginBottom: '1rem' }}>
        <SurfaceHeader>
          <Toolbar style={{ flex: 1, justifyContent: 'space-between' }}>
            <div>
              <Muted style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase' }}>
                Fuente OBS (fondo transparente)
              </Muted>
            </div>
            <Badge $active={pending.length > 0}>{pending.length} pendiente(s)</Badge>
          </Toolbar>
        </SurfaceHeader>
        <SurfaceBody>
          <FormRow>
            <Label>URL monitor</Label>
            <Toolbar style={{ gap: '0.45rem' }}>
              <Input readOnly value={monitorUrl} style={{ flex: 1, minWidth: 0 }} />
              <Button $size="sm" type="button" onClick={() => copy(monitorUrl)}>Copiar</Button>
              <Button $size="sm" $variant="ghost" type="button" onClick={regenerate}>Regenerar</Button>
            </Toolbar>
          </FormRow>
          <Muted style={{ marginTop: '0.5rem' }}>
            En OBS: Fuente → Navegador → pegá la URL (viene del backend, no hace falta rebuild web).
            Ancho ~420, alto ~320. CSS custom:
          </Muted>
          <pre style={{ margin: '0.5rem 0 0', padding: '0.65rem', borderRadius: 8, background: 'rgba(0,0,0,0.35)', fontSize: '0.78rem', overflow: 'auto' }}>
            {OBS_CSS}
          </pre>
        </SurfaceBody>
      </Surface>

      <Surface>
        <SurfaceBody $flush>
          {!pending.length ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9a8aa8' }}>
              Sin pedidos pendientes
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.85rem' }}>
              {pending.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto',
                    gap: '0.75rem',
                    alignItems: 'center',
                    padding: '0.75rem 0.85rem',
                    borderRadius: 12,
                    border: '1px solid rgba(255, 20, 147, 0.25)',
                    background: 'rgba(255, 20, 147, 0.06)',
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: '#fff' }}>
                      {c.displayName}
                      <span style={{ opacity: 0.55, marginLeft: 8, fontWeight: 400 }}>@{c.username}</span>
                    </div>
                    <div style={{ marginTop: 4, color: '#ff8cc8', fontSize: '0.9rem' }}>
                      {c.quantity} oráculo{c.quantity !== 1 ? 's' : ''}
                      {c.note ? ` · "${c.note}"` : ''}
                    </div>
                    <Muted style={{ margin: '0.25rem 0 0', fontSize: '0.75rem' }}>
                      {new Date(c.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </Muted>
                  </div>
                  <ActionBar>
                    <Button
                      $size="sm"
                      type="button"
                      disabled={busyId === c.id}
                      onClick={() => deliver(c.id)}
                    >
                      Entregado
                    </Button>
                    <Button
                      $size="sm"
                      $variant="ghost"
                      type="button"
                      disabled={busyId === c.id}
                      onClick={() => reject(c.id)}
                    >
                      No entregado
                    </Button>
                  </ActionBar>
                </div>
              ))}
            </div>
          )}
        </SurfaceBody>
      </Surface>
    </Page>
  );
};

export default OracleMonitorPage;
